import { useState, ReactNode } from "react";
import { Menu, X } from "lucide-react";

const navLinks = [
  { name: "What We Solve", href: "#solutions" },
  { name: "How We Work", href: "#process" },
  { name: "Pricing", href: "#pricing" },
  { name: "About", href: "#about" },
];

const scrollToSection = (href: string) => {
  const id = href.replace("#", "");
  const element = document.getElementById(id);
  if (element) {
    const offset = 80; // Account for sticky navbar height
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
  }
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
};

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    scrollToSection(href);
  };

  const handleMobileNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    // Small delay to allow menu to close before scrolling
    setTimeout(() => scrollToSection(href), 100);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button 
              onClick={scrollToTop}
              className="flex items-center gap-2 group"
              data-testid="nav-logo"
            >
              <img 
                src="/logo.png" 
                alt="MediaSwift" 
                className="h-10 w-auto transition-transform group-hover:scale-105"
              />
              <span className="text-[#002D62] font-bold text-xl tracking-tight">MediaSwift</span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className="text-[#002D62] font-medium transition-all relative py-2 hover:text-[#D7A600]"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => handleNavClick(e, "#contact")}
                data-testid="nav-cta-button"
                className="bg-[#002D62] text-white font-bold px-6 py-2.5 rounded-md transition-all hover:bg-[#003A80] hover:scale-105"
              >
                Get in Touch
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-[#002D62] p-2"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#E5E7EB]" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleMobileNavClick(e, link.href)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className="block py-3 px-4 rounded-lg text-[#002D62] font-medium transition-all hover:bg-[#F5F7FA]"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => handleMobileNavClick(e, "#contact")}
                data-testid="mobile-nav-cta-button"
                className="block w-full text-center bg-[#002D62] text-white font-bold py-3 rounded-lg mt-4 transition-all hover:bg-[#003A80]"
              >
                Get in Touch
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Logo & Tagline */}
            <div>
              <button onClick={scrollToTop} className="flex items-center gap-2 group">
                <img 
                  src="/logo.png" 
                  alt="MediaSwift" 
                  className="h-10 w-auto transition-transform group-hover:scale-105"
                />
                <span className="text-[#002D62] font-bold text-xl tracking-tight">MediaSwift</span>
              </button>
              <p className="mt-4 text-[#002D62]/70 leading-relaxed">
                Operational software that solves real problems.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-[#002D62] font-bold text-sm uppercase tracking-wider mb-4">Quick Links</h4>
              <nav className="space-y-3">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}-link`}
                    className="block text-[#002D62]/70 hover:text-[#D7A600] transition-colors"
                  >
                    {link.name}
                  </a>
                ))}
              </nav>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-[#002D62] font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
              <div className="space-y-3">
                <a
                  href="mailto:info@mediaswift.net"
                  data-testid="footer-email-link"
                  className="block text-[#002D62]/70 hover:text-[#D7A600] transition-colors"
                >
                  info@mediaswift.net
                </a>
                <span
                  data-testid="footer-website-text"
                  className="block text-[#002D62]/70"
                >
                  mediaswift.net
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#E5E7EB]">
          <div className="bg-[#F5F7FA] py-6">
            <p className="text-[#002D62] text-sm text-center">
              © 2025 MediaSwift. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
